import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import '../styles/landing.css'
import { trackPageView } from '../tracker'
import "../styles/main.scss";

const LandingPage = () => {
  const slides = [
    { img: 'icons/sc1.png', title: '', desc: '', isFirst: true, icon: 'icons/icons/slide-1.svg' },
    { img: 'icons/sc2.png', icon: 'icons/icons/slide-2.svg', title: 'Intelligence Feed', desc: 'Real-time event stream with conflict classification, severity tagging, casualty breakdowns, and expandable incident details including geolocation, source attribution, and event mapping. Each event is enriched with contextual analysis to support rapid decision-making. Filter by country, violence type, and custom date ranges across all monitored regions.' },
    { img: 'icons/sc8.png', icon: 'icons/icons/slide-3.svg', title: 'Daily Incidents & Threat Analytics', desc: 'Event frequency charts with casualty overlay, cumulative threat level tracking, and multi-tab analytics covering Events, Casualties, Threat Level, and Timeline views. Identify escalation patterns and conflict intensity shifts over configurable time periods. Major events panel highlights critical developments with real-time severity indicators.' },
    { img: 'icons/sc7.png', icon: 'icons/icons/slide-4.svg', title: 'Intelligence & Forecast Reports', desc: 'Analyst-grade intelligence reports combining incident overviews, strategic implications, and escalation context. Each report cross-references conflict actors, supply chains, and geopolitical dynamics. Covers forward-looking threat assessments with actionable recommendations. Exportable and shareable across your organization for coordinated response planning.' },
    { img: 'icons/sc6.png', icon: 'icons/icons/slide-5.svg', title: 'Threat Score & Impact Zones', desc: 'Country-level threat scoring on a 1-10 scale with potential target mapping across cities and strategic assets. Critical infrastructure identification including airports, seaports, refineries, military installations, nuclear facilities, and financial centers. Each zone is assessed for attack probability based on historical patterns, actor capabilities, and current escalation trajectory.' },
    { img: 'icons/sc5.png', icon: 'icons/icons/slide-6.svg', title: 'Satellite Damage Assessment', desc: 'Post-strike satellite imagery analysis combining NASA FIRMS thermal detection, before/after visual comparison, and AI-powered damage classification — delivered within hours. Impact zones are auto-detected through hotspot clustering and cross-referenced with points of interest. Each assessment includes a consolidated impact assessment with GEOINT analyst summary and operational recommendations.' },
    { img: 'icons/sc3.png', icon: 'icons/icons/slide-7.svg', title: 'Evacuation Protocols', desc: 'Pre-planned extraction routes with safe haven identification, airport operational status monitoring via FlightRadar24, and multi-route alternatives across the GCC region. Routes factor in real-time airspace disruptions, embassy proximity, and hospital locations. Designed for rapid deployment when situations escalate beyond acceptable risk thresholds for your personnel on the ground.' },
    { img: 'icons/sc4.png', icon: 'icons/icons/slide-8.svg', title: 'Personnel Alerting', desc: 'Live field personnel tracking with SOS trigger capability, automated zone warnings, and periodic check-in monitoring. Real-time positioning of team members across monitored regions with signal strength indicators and last activity timestamps. Critical zone alerts are pushed instantly via SMS and Telegram when personnel enter high-risk areas. Restricted feature available on full access plans.' },
    { img: 'icons/sc10.png', icon: 'icons/icons/slide-9.svg', title: 'Weapons Deployment Timeline', desc: 'Track weapon systems deployment over time with escalation ladder indicators. Progression analysis from low-sophistication drones to ballistic missiles and potential WMD signals capability evolution and intent shifts. The timeline correlates weapons usage with conflict phases, helping analysts forecast next-stage escalation risks and adjust defensive posture requirements accordingly.' },
    { img: 'icons/sc9.png', icon: 'icons/icons/slide-10.svg', title: 'Arsenal Database', desc: 'Comprehensive catalog of offensive and defensive military systems with technical specifications, manufacturer details, operator country mapping, and confirmed deployment counts. Covers missiles, drones, air defense systems, radar installations, and naval assets. Cross-referenced with active conflict data to identify which systems are being deployed in each theater of operations across monitored regions.' },
  ];
  const [slideIdx, setSlideIdx] = useState(0);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    if (!window.L) return;
    const L = window.L;
    const map = L.map(mapRef.current, {
      zoomControl: false, attributionControl: false, dragging: false,
      scrollWheelZoom: false, doubleClickZoom: false, touchZoom: false,
      minZoom: 1, maxZoom: 4, maxBounds: [[-40, -180], [72, 180]], maxBoundsViscosity: 1,
    }).setView([25, 30], 2);

    L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_nolabels/{z}/{x}/{y}@2x.png', {
      maxZoom: 6, opacity: 0.8, noWrap: true,
    }).addTo(map);

    // Service regions - GeoJSON polygons approximating coverage from original map
    // Middle East & North Africa
    const meRegions = [
      // GCC + Middle East (large block)
      [[37,24],[37,63],[12,63],[12,34],[22,34],[30,24]],
      // North Africa
      [[37,10],[37,24],[30,24],[22,34],[12,34],[12,-17],[20,-17],[37,10]],
      // East Africa
      [[12,34],[12,52],[-15,52],[-15,28],[4,28],[12,34]],
      // West Africa
      [[20,-17],[20,16],[4,16],[4,-17]],
      // Central Africa
      [[12,8],[12,32],[4,32],[-6,32],[-6,8]],
      // Afghanistan/Pakistan
      [[38,60],[38,78],[24,78],[24,60]],
      // Ukraine/Russia region
      [[55,22],[55,45],[44,45],[44,22]],
      // Myanmar/SE Asia
      [[28,92],[28,102],[14,102],[14,92]],
      // Latin America
      [[20,-60],[20,-117],[32,-117],[32,-85],[-5,-85],[-5,-35],[-25,-35],[-25,-60]],
    ];

    meRegions.forEach((coords, i) => {
      const latLngs = coords.map(c => [c[0], c[1]]);
      L.polygon(latLngs, {
        fillColor: '#C29717',
        fillOpacity: i < 3 ? 0.18 : 0.08,
        color: '#C29717',
        weight: 0.5,
        opacity: 0.15,
      }).addTo(map);
    });

    // Match map bg to section
    mapRef.current.style.background = '#262626';
    const leafletBg = mapRef.current.querySelector('.leaflet-container');
    if (leafletBg) leafletBg.style.background = '#262626';
    // Apply pulse animation to glow circles
    setTimeout(() => {
      const glows = mapRef.current?.querySelectorAll('.coverage-glow');
      if (glows) glows.forEach((g, i) => {
        g.style.animation = 'coveragePulse ' + (3 + (i % 3)) + 's ease-in-out infinite';
        g.style.animationDelay = (i * 0.2) + 's';
        g.style.transformOrigin = 'center';
      });
    }, 1000);
    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 500);
  }, []);
  
  const apiHost = process.env.REACT_APP_APIHOST || 'http://localhost:3033';
  const [cf, setCf] = useState({firstName:'',lastName:'',email:'',company:'',phone:'',message:''});
  const [cfSent, setCfSent] = useState(false);
  const [cfSending, setCfSending] = useState(false);
  const submitContact = async (e) => {
    e.preventDefault();
    if(!cf.firstName||!cf.email) return;
    setCfSending(true);
    try { await axios.post(apiHost+'/contact/inquiry',{...cf, subject:'Partner inquiry'}); setCfSent(true); } catch(err){}
    setCfSending(false);
  };
  useEffect(() => {
    document.body.className = 'landing-page';
    // Pulse animation for coverage markers
    if (!document.querySelector('#coverage-pulse-css')) {
      const style = document.createElement('style');
      style.id = 'coverage-pulse-css';
      style.textContent = '@keyframes coveragePulse { 0%,100% { opacity:0.6; transform:scale(1); } 50% { opacity:0.2; transform:scale(1.6); } }';
      document.head.appendChild(style);
    }
    // Load Leaflet for coverage map
    if (!document.querySelector('link[href*="leaflet"]')) {
      const lc = document.createElement('link'); lc.rel='stylesheet'; lc.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(lc);
      const ls = document.createElement('script'); ls.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; document.head.appendChild(ls);
    }; trackPageView('/');

     setTimeout(() => {
    if (window.initParallaxAnimations) {
      window.initParallaxAnimations();
    }
  }, 100);
    
    return () => {
      document.body.className = '';
    };
  }, []);

    

  return (
    <>
      {/* header start */}
      <header className="header" id="header">
        <div className="auto__container">
          <div className="header__inner">
            <a href="index.html" className="header__inner-logo">
              <img src="icons/logo.webp" alt="logo" />
            </a>
            <nav className="nav" id="menu">
              <ul className="nav__inner">
                <li><a href="#services" className="links">SERVICES</a></li>
                <li><a href="#coverage" className="links">COVERAGE</a></li>
                <li><a href="#partners" className="links">PARTNERS</a></li>
                <li><a href="#contacts" className="links">CONTACTS</a></li>
                <li><Link to="/daily-reports" className="links dr-nav-link"><span className="radar-icon"><span className="radar-ping"></span><span className="radar-dot"></span></span>DAILY REPORTS</Link></li>
              </ul>
            </nav>
            <div className="header__inner-side">
<Link to="/login" className="header__inner-link">LOGIN</Link>
              <div className="burger" id="menuBtn"><span></span></div>
            </div>
          </div>
        </div>
      </header>
      {/* header end */}

      <main className="main">
        {/* hero start */}
        <section className="hero">
          <div className="hero__bg">
            <img src="icons/bg-1.webp" alt="bg" />
            <div className="hero__bg-inner">
              <div className="hero__bg-shape">
                <img src="icons/shape-1.webp" alt="shape" />
              </div>
            </div>
          </div>
          <div className="auto__container">
            <div className="hero__inner">
              <div className="hero__inner-title">
                <span><img src="icons/icons/polygon-big.svg" alt="polygon" /></span>
                <h1>Navigating risk with strategic foresight.</h1>
              </div>
              <div className="hero__inner-text">
                <p>
                  FORTE was founded with the ambition of refining strategic
                  intelligence to better support organizations.
                  <br /><br />
                  Our mission — to enhance their contextual integration in
                  the countries where they operate, professionalize on-the-ground
                  support in complex environments, and assist our partners in
                  developing effective security strategies.
                </p>
              </div>
              <div className="hero__inner-icons">
                <span><img src="icons/icons/icon-1.svg" alt="icon" /></span>
                <span><img src="icons/icons/icon-2.svg" alt="icon" /></span>
                <span><img src="icons/icons/icon-3.svg" alt="icon" /></span>
                <span><img src="icons/icons/icon-4.svg" alt="icon" /></span>
                <span><img src="icons/icons/icon-5.svg" alt="icon" /></span>
              </div>
            </div>
          </div>
        </section>
        {/* hero end */}

        {/* info start */}
        <section className="info">
          <div className="auto__container">
            <div className="info__inner">
              <div className="info__inner-image">
                <div style={{position:'relative'}}>
                  <img src={slides[slideIdx].img} alt={slides[slideIdx].title} style={{width:'100%',borderRadius:6,border:'1px solid rgba(255,255,255,0.06)',transition:'opacity 0.5s ease',boxShadow:'0 8px 32px rgba(0,0,0,0.4)',display:'block'}} />
                  <div onClick={() => setSlideIdx(p => p === 0 ? slides.length - 1 : p - 1)} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',width:32,height:32,borderRadius:'50%',background:'rgba(0,0,0,0.5)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#aaa',fontSize:14,userSelect:'none'}}>&#8249;</div>
                  <div onClick={() => setSlideIdx(p => (p + 1) % slides.length)} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',width:32,height:32,borderRadius:'50%',background:'rgba(0,0,0,0.5)',border:'1px solid rgba(255,255,255,0.1)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#aaa',fontSize:14,userSelect:'none'}}>&#8250;</div>
                </div>
              </div>
              <div className="info__inner-text" style={{paddingTop:8}}>
                {/* Slide indicators with icons */}
                <div style={{display:'flex',gap:3,marginBottom:16,alignItems:'center'}}>
                  {slides.map((s, i) => (
                    <div key={i} onClick={() => setSlideIdx(i)} style={{
                      display:'flex',alignItems:'center',justifyContent:'center',
                      width: 28, height: 22, borderRadius: 3, cursor: 'pointer', transition: 'all 0.3s',
                      background: i === slideIdx ? 'rgba(194,151,23,0.12)' : 'transparent',
                      borderBottom: i === slideIdx ? '2px solid #C29717' : '2px solid rgba(255,255,255,0.04)',
                      opacity: i === slideIdx ? 1 : 0.3,
                      filter: i === slideIdx ? 'none' : 'grayscale(1)'
                    }}>
                      {s.icon && <img src={s.icon} alt="" style={{width:14,height:11,opacity: i === slideIdx ? 0.9 : 0.4}} />}
                    </div>
                  ))}
                </div>
                {slides[slideIdx].isFirst ? (
                  <>
                    <p>
                      FORTE platform offers the capability to track significant events
                      globally, including those involving casualties or strategic
                      impact, with clear contextual analysis of each incident.
                    </p>
                    <p style={{marginTop: '16px'}}>
                      It allows the creation of custom monitoring zones and event
                      filters, ensuring organizations are immediately alerted to
                      developments that may affect their employees, clients, or local
                      operations. Designed for situational awareness and operational
                      continuity, it transforms raw data into relevant, actionable
                      insight.
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:10,color:'#C29717',textTransform:'uppercase',letterSpacing:2,marginBottom:8}}>{slides[slideIdx].title}</div>
                    <p style={{fontSize:14,lineHeight:1.7}}>
                      {slides[slideIdx].desc}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
        {/* info end */}

        {/* mission start */}
        <div className="anchor" id="mission"></div>
        <section className="mission">
          <div className="mission__bg">
            <img src="icons/bg-2.webp" alt="bg" />
          </div>
          <div className="auto__container">
            <div className="mission__inner">
              <div className="mission__inner-title">
                <h2>MISSION</h2>
              </div>
              <div className="mission__inner-row">
                <div className="mission__inner-image">
                  <img src="icons/mission.webp" alt="mission" />
                </div>
                <div className="missionItem">
                  <div className="missionItem__title">
                    <span><img src="icons/icons/polygon.svg" alt="polygon" /></span>
                    <h4>Illuminate complexity with strategic intelligence.</h4>
                  </div>
                  <div className="missionItem__text">
                    <p>
                      In a world clouded by noise and disinformation, FORTE brings
                      clarity. We deliver intelligence that is not only timely,
                      but relevant—crafted to reveal underlying dynamics, expose
                      unseen risks, and support decisions that shape resilient
                      futures. We don't just report facts; we connect them,
                      helping our partners see what others miss.
                    </p>
                  </div>
                </div>
                <div className="missionItem">
                  <div className="missionItem__title">
                    <span><img src="icons/icons/polygon.svg" alt="polygon" /></span>
                    <h4>Operate where others hesitate.</h4>
                  </div>
                  <div className="missionItem__text">
                    <p>
                      FORTE is built on the ground, not from behind a screen. Our
                      strength lies in a decentralized network of regional experts
                      embedded in the world’s most challenging
                      environments—conflict zones, unstable states, regions ruled
                      by volatility. We navigate these shadows daily, offering our
                      clients the rare advantage of proximity, context, and
                      action.
                    </p>
                  </div>
                </div>
                <div className="missionItem big">
                  <div className="missionItem__title">
                    <span><img src="icons/icons/polygon.svg" alt="polygon" /></span>
                    <h4>Turn strategy into protection.</h4>
                  </div>
                  <div className="missionItem__text">
                    <p>
                      We believe security must be lived, not theorized. FORTE
                      designs and deploys strategies that are practical, tested,
                      and field-ready. Whether it’s developing crisis protocols,
                      training frontline staff, or managing evacuation logistics,
                      we transform complex threats into structured, human-centered
                      responses that protect people, operations, and reputation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* mission end */}

        {/* services start */}
        <div className="anchor" id="services"></div>
        <section className="services">
          <div className="services__bg">
            <div className="services__bg-shape">
              <img src="icons/shape-2.webp" alt="shape" />
            </div>
          </div>
          <div className="auto__container">
            <div className="services__inner">
              <div className="services__inner-title">
                <h2>SERVICES</h2>
              </div>
              <div className="services__inner-row">
                {[
                  {
                    icon: "icon-1-dark.svg",
                    title: "INTEL",
                    text: `FORTE delivers strategic and operational intelligence designed
                    to anticipate threats before they materialize. Our global
                    network of analysts and local sources provides real-time
                    monitoring, contextual analysis, and tailored
                    reporting—empowering organizations to make informed decisions
                    in volatile environments. Whether tracking political
                    instability, security incidents, or criminal activity, our
                    intelligence is actionable, precise, and directly connected to
                    what unfolds on the ground.`,
                  },
                  {
                    icon: "icon-2-dark.svg",
                    title: "TRAVEL RISK MANAGEMENT",
                    text: `FORTE supports safe mobility through comprehensive Travel Risk
                    Management services. From pre-trip briefings and route
                    assessments to real-time alerting and emergency response, we
                    ensure personnel are informed, monitored, and protected
                    throughout their journey. In complex or unstable regions, we
                    offer enhanced capabilities including secure transport,
                    medical support, and local extraction protocols, providing
                    peace of mind wherever your operations require presence.`,
                  },
                  {
                    icon: "icon-3-dark.svg",
                    title: "GROUND SUPPORT",
                    text: `Our ground support teams operate where your teams do—not from
                    a distance, but side by side. Whether in conflict zones,
                    politically sensitive environments, or regions affected by
                    organized crime or natural disaster, FORTE deploys operational
                    specialists and local networks to ensure the continuity of
                    missions, the security of personnel, and the real-time
                    execution of contingency plans. We turn intelligence into
                    action, directly on the field.`,
                  },
                  {
                    icon: "icon-4-dark.svg",
                    title: "MANAGEMENT & CORPORATE SECURITY SERVICES",
                    text: `FORTE offers end-to-end corporate and operational security
                    solutions, tailored to the unique risk landscape of each
                    client. This includes security audits, risk governance
                    frameworks, crisis management protocols, executive protection,
                    and training programs for staff in exposed roles. We help
                    organizations integrate security into their core strategy,
                    ensuring not just protection, but resilience and continuity
                    across all levels of leadership and operations.`,
                  },
                  {
                    icon: "icon-5-dark.svg",
                    title: "EMERGENCY MEDICAL ASSISTANCE",
                    text: `FORTE provides rapid and reliable emergency medical assistance
                    in high-risk and remote environments. From on-site medical
                    stabilization to medical evacuation by air or ground, our
                    teams ensure immediate care when and where it matters most.
                    Backed by a vetted global network of clinics, doctors, and
                    response units, we support organizations facing medical crises
                    with speed, coordination, and discretion—protecting lives
                    while maintaining operational continuity.`,
                  },
                ].map(({ icon, title, text }, i) => (
                  <div key={i} className="servicesItem">
                    <div className="servicesItem__top">
                      <span><img src={`icons/icons/${icon}`} alt="icon" /></span>
                      <h3>{title}</h3>
                    </div>
                    <div className="servicesItem__text">{text}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* services end */}

        {/* coverage start */}
        <div className="anchor" id="coverage"></div>
        <section className="coverage" style={{background:"#262626"}}>
          <div className="coverage__bg" style={{background:'#262626',zIndex:0}}></div>
          <div className="auto__container">
            <div className="coverage__inner">
              <div className="coverage__inner-title">
                <h2>COVERAGE</h2>
                <p>
                  <span><img src="icons/icons/polygon-big.svg" alt="polygon" /></span>
                  FORTE operates 24/7, 365 days a year to ensure uninterrupted
                  support and response wherever you are.
                </p>
              </div>
              <div className="coverage__inner-image" style={{position:'relative',overflow:'visible',border:'none',margin:'0 -80px'}}>
                <div ref={mapRef} style={{width:'calc(100% + 160px)',height:500,background:'#262626'}}></div>
              </div>
              <div className="coverage__inner-text">
                <p>
                  FORTE operates in over 60 countries, with primary zones of
                  intervention across MENA, Sub-Saharan Africa, Latin America, and
                  Southeast Asia.
                  <br /><br />
                  We cover a wide spectrum of risks, including:
                </p>
                <ul>
                  <li>Political instability and regime change</li>
                  <li>Organized crime and kidnapping</li>
                  <li>Environmental and climate-driven hazards</li>
                  <li>Health crises and pandemics</li>
                  <li>Technological threats and infrastructure failure</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* coverage end */}

        {/* partners start */}
        <div className="anchor" id="partners"></div>
        <section className="partners">
          <div className="auto__container">
            <div className="partners__inner">
              <div className="partners__inner-title">
                <h2>CONTACT</h2>
                <p>
                  Get in touch with our team to discuss your intelligence and risk monitoring needs.
                </p>
              </div>
              {cfSent ? (
                <div style={{textAlign:'center',padding:'40px 0'}}>
                  <div style={{fontSize:18,color:'#4CAF50',marginBottom:8}}>Message sent</div>
                  <div style={{fontSize:13,color:'#888'}}>Our team will get back to you shortly.</div>
                </div>
              ) : (
              <form className="partnersForm" onSubmit={submitContact}>
                <div className="partnersForm__row">
                  <div className="input__outer">
                    <p>FIRSTNAME</p>
                    <div className="input">
                      <input type="text" required value={cf.firstName} onChange={e=>setCf({...cf,firstName:e.target.value})} />
                    </div>
                  </div>
                  <div className="input__outer">
                    <p>LASTNAME</p>
                    <div className="input">
                      <input type="text" value={cf.lastName} onChange={e=>setCf({...cf,lastName:e.target.value})} />
                    </div>
                  </div>
                  <div className="input__outer big">
                    <p>COMPANY NAME</p>
                    <div className="input">
                      <input type="text" value={cf.company} onChange={e=>setCf({...cf,company:e.target.value})} />
                    </div>
                  </div>
                  <div className="input__outer">
                    <p>EMAIL</p>
                    <div className="input">
                      <input type="email" required value={cf.email} onChange={e=>setCf({...cf,email:e.target.value})} />
                    </div>
                  </div>
                  <div className="input__outer">
                    <p>PHONE NUMBER</p>
                    <div className="input">
                      <input type="tel" value={cf.phone} onChange={e=>setCf({...cf,phone:e.target.value})} />
                    </div>
                  </div>
                  <div className="input__outer big">
                    <p>MESSAGE</p>
                    <div className="input">
                      <textarea rows="6" value={cf.message} onChange={e=>setCf({...cf,message:e.target.value})}></textarea>
                    </div>
                  </div>
                </div>
                <div className="partnersForm__foot">
                  <button type="submit" className="button primary" disabled={cfSending}>
                    <span><img src="icons/icons/send.svg" alt="send" /></span>
                    {cfSending ? 'SENDING...' : 'SEND'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </section>
        {/* partners end */}

        {/* footer start */}
        <div className="anchor" id="contacts"></div>
        <footer className="footer">
          <div className="footer__bg">
            <div className="footer__bg-shape">
              <img src="icons/shape-3.webp" alt="shape" />
            </div>
          </div>
          <div className="auto__container">
            <div className="footer__inner">
              <div className="footer__inner-top">
                <h2>REACH OUT</h2>
              </div>
              <div className="footer__inner-row">
                <div className="footer__inner-content">
                  <div className="footer__inner-title">
                    <span><img src="icons/icons/polygon-big.svg" alt="polygon" /></span>
                    <p>Get in touch with FORTE by Phone.</p>
                  </div>
                  <div className="footer__inner-info">
                    <p>
                      Paris <br />
                      +33 (0)7 78 48 72 33
                      <br /><br />
                      London <br />
                      +44 (0) 74 88 84 49 76
                      <br /><br />
                      Mexico <br />
                      +52 (0) 55 85 26 33 89
                      <br /><br /><br />
                      United Arab Emirates <br />
                      +971 (0)5 23 75 76 35
                    </p>
                  </div>
                </div>
                <div className="footer__inner-col">
                  <div className="footer__inner-title">
                    <span><img src="icons/icons/polygon-big.svg" alt="polygon" /></span>
                    <p>By Email</p>
                  </div>
                  <div className="footer__inner-mails">
                    <p>
                      <a href="mailto:business@forteintel.com">business@forteintel.com</a>
                    </p>
                  </div>
                </div>
              </div>
              <div className="footer__inner-copy">
                <div className="footer__inner-side">
                  <div className="footer__inner-logo">
                    <img src="icons/logo.webp" alt="logo" />
                  </div>
                  <p>FORTE INTEL | 2026</p>
                </div>
                <div className="footer__inner-links">
                  <a href="#services">SERVICES</a>
                  <a href="#coverage">COVERAGE</a>
                  <a href="#partners">PARTNERS</a>
                  <a href="#contact">CONTACTS</a>
                  <Link to="/daily-reports" className="dr-nav-link"><span className="radar-icon"><span className="radar-ping"></span><span className="radar-dot"></span></span>DAILY REPORTS</Link>
                  <Link to="/privacy" className="footer__inner-log">PRIVACY</Link>
                </div>
                <Link to="/login" className="footer__inner-log">LOGIN</Link>
              </div>
            </div>
          </div>
        </footer>
        {/* footer end */}
      </main>
    </>
  );
};

export default LandingPage;
